import React, { useEffect, useState } from 'react';
import { Button, Checkbox, Modal, Spin, message } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../i18n';
import { getCollaborationDetail, removeCollaborationCreators } from '../../../store/request';
import type { TargetCollaborationCreator } from '../../../types/collaboration';
import { CreatorAcceptTag } from './CreatorAcceptTag';

type RemoveInviteTalentModalProps = {
  open: boolean;
  shopId?: string | null;
  collaborationIds: number[];
  onClose: () => void;
  onSuccess?: () => void;
};

function creatorName(creator: TargetCollaborationCreator) {
  return (
    creator.nickname?.trim() ||
    creator.platformNickname?.trim() ||
    creator.creatorName?.trim() ||
    creator.username?.trim() ||
    creator.platformUsername?.trim() ||
    creator.creatorOpenId?.trim() ||
    '—'
  );
}

function creatorMeta(creator: TargetCollaborationCreator) {
  const handle = (creator.username?.trim() || creator.platformUsername?.trim() || '').replace(/^@/, '');
  const region = creator.selectionRegion?.trim() || '';
  return [handle ? `@${handle}` : '', region].filter(Boolean).join(' · ');
}

function creatorKey(creator: TargetCollaborationCreator) {
  return creator.influencerId?.trim() || creator.creatorOpenId?.trim() || '';
}

function isUnaccepted(creator: TargetCollaborationCreator) {
  return creator.label === 4;
}

export function RemoveInviteTalentModal({
  open,
  shopId,
  collaborationIds,
  onClose,
  onSuccess
}: RemoveInviteTalentModalProps) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [creators, setCreators] = useState<TargetCollaborationCreator[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const currentShopId = shopId?.trim() || '';

  useEffect(() => {
    if (!open) return;
    const inviteIds = Array.from(new Set(collaborationIds.filter((id) => Number.isInteger(id))));
    if (!currentShopId || inviteIds.length === 0) {
      setCreators([]);
      setSelectedIds([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setCreators([]);
    setSelectedIds([]);
    Promise.all(inviteIds.map((id) => getCollaborationDetail(id, { shopId: currentShopId })))
      .then((results) => {
        if (cancelled) return;
        const failed = results.find((res) => res.code !== 200);
        if (failed) {
          if (failed.code !== -1) message.error(failed.message || t('invitation.detail_load_failed'));
          setCreators([]);
          setSelectedIds([]);
          return;
        }
        const byId = new Map<string, TargetCollaborationCreator>();
        results.forEach((res) => {
          const list = res.content?.creators;
          if (!Array.isArray(list)) return;
          list.forEach((creator) => {
            const id = creatorKey(creator);
            if (!id || byId.has(id)) return;
            byId.set(id, creator);
          });
        });
        const next = Array.from(byId.values());
        setCreators(next);
        setSelectedIds(
          next
            .filter((creator) => isUnaccepted(creator))
            .map((creator) => creator.creatorOpenId?.trim() || '')
            .filter(Boolean)
        );
      })
      .catch(() => {
        if (!cancelled) {
          message.error(t('invitation.detail_load_failed'));
          setCreators([]);
          setSelectedIds([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, currentShopId, collaborationIds, t]);

  const hasUnaccepted = creators.some(isUnaccepted);

  const toggleCreator = (id: string, checked: boolean) => {
    setSelectedIds((prev) => (checked ? (prev.includes(id) ? prev : [...prev, id]) : prev.filter((item) => item !== id)));
  };

  const submit = async () => {
    if (submitting || loading) return;
    if (!currentShopId) {
      message.warning(t('invitation.invite_shop_required'));
      return;
    }
    const inviteIds = Array.from(new Set(collaborationIds.filter((id) => Number.isInteger(id))));
    if (inviteIds.length === 0) {
      message.warning(t('invitation.invite_pick_required'));
      return;
    }
    if (inviteIds.length > 10) {
      message.warning(t('invitation.invite_limit'));
      return;
    }
    const creatorOpenIds = Array.from(new Set(selectedIds.map((id) => id.trim()).filter(Boolean)));
    if (creatorOpenIds.length === 0) {
      message.warning(t('invitation.pick_talent'));
      return;
    }
    setSubmitting(true);
    try {
      const res = await removeCollaborationCreators({
        shopId: currentShopId,
        collaborationIds: inviteIds,
        creatorOpenIds
      });
      if (res.code !== 200) {
        if (res.code !== -1) message.error(res.message || t('invitation.remove_talent_failed'));
        return;
      }
      const result = res.content;
      const noneRemoved =
        result != null &&
        (result.successCount ?? 0) === 0 &&
        (result.partialSuccessCount ?? 0) === 0 &&
        (result.failureCount ?? 0) > 0;
      if (noneRemoved) {
        message.warning(res.message || t('invitation.remove_talent_failed'));
        return;
      }
      message.success(res.message || t('invitation.remove_talent_success'));
      onSuccess?.();
      onClose();
    } catch {
      message.error(t('invitation.remove_talent_failed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={t('invitation.remove_talent_title')}
      open={open}
      onCancel={onClose}
      destroyOnClose
      centered
      width={560}
      styles={{ body: { maxHeight: 'calc(100vh - 180px)', overflowY: 'auto' } }}
      closeIcon={
        <span className='cursor-pointer text-sm font-normal text-slate-500 hover:text-slate-700'>{t('common.close')}</span>
      }
      footer={
        <div className='flex justify-end gap-2'>
          <Button className='rounded-lg' onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            danger
            type='primary'
            className='rounded-lg'
            loading={submitting}
            disabled={loading || !hasUnaccepted}
            onClick={submit}
          >
            {t('invitation.remove_talent')}
          </Button>
        </div>
      }>
      <div className='space-y-3'>
        {/* <p className='mb-0 text-xs leading-relaxed text-slate-400'>{t('invitation.remove_talent_hint')}</p>
        <p className='mb-0 text-xs leading-relaxed text-slate-400'>
          {selectedIds.length === 0
            ? t('invitation.none_selected')
            : t('invitation.selected_count', { count: selectedIds.length })}
        </p> */}
        {!loading && !hasUnaccepted ? (
          <div className='flex items-center gap-2 rounded-lg border border-[#d6e4ff] bg-[#f5f8ff] px-3 py-2.5 text-sm leading-relaxed text-[#3b4dd7]'>
            <InfoCircleOutlined className='shrink-0' />
            <span>{t('invitation.remove_no_unaccepted')}</span>
          </div>
        ) : null}
        {loading || creators.length > 0 ? (
        <div className='max-h-[280px] overflow-y-auto rounded-lg border border-[#eef0f4] bg-[#fafbfc] px-2 py-1'>
          {loading ? (
            <div className='flex justify-center py-6'>
              <Spin size='small' />
            </div>
          ) : null}
          {!loading
            ? creators.map((creator, index) => {
              const openId = creator.creatorOpenId?.trim() || '';
              const id = creatorKey(creator);
              const name = creatorName(creator);
              const meta = creatorMeta(creator);
              const removable = isUnaccepted(creator) && Boolean(openId);
              return (
                <label
                  key={id || `creator-${index}`}
                  className='flex cursor-pointer items-center gap-3 rounded-md px-2 py-2.5 hover:bg-white'>
                  <Checkbox
                    checked={removable && selectedIds.includes(openId)}
                    disabled={!removable}
                    onChange={(e) => toggleCreator(openId, e.target.checked)}
                  />
                  {creator.avatarUrl?.trim() ? (
                    <img
                      src={creator.avatarUrl.trim()}
                      alt=''
                      className='size-8 shrink-0 rounded-full object-cover'
                    />
                  ) : (
                    <span className='inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-[#596ae8] text-xs font-semibold text-white'>
                      {name.slice(0, 1).toUpperCase() || '?'}
                    </span>
                  )}
                  <span className='min-w-0 flex-1'>
                    <span className='flex items-center gap-2'>
                      <span className='min-w-0 truncate text-sm font-semibold text-slate-800'>{name}</span>
                      <CreatorAcceptTag label={creator.label} />
                    </span>
                    {meta ? <span className='block truncate text-xs text-slate-500'>{meta}</span> : null}
                  </span>
                </label>
              );
            })
            : null}
        </div>
        ) : null}
      </div>
    </Modal>
  );
}
